import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Image as ImageIcon,
  LayoutGrid,
  LayoutList,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useTheme } from "../../context/ThemeContext";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { runTaskToCompletion } from "../../services/taskService";
import { normalizeWanxImageQualityTier } from "../../utils/wanxImageCapabilities";

const PLAN_FIELDS = [
  ["title", "标题"],
  ["storyPurpose", "叙事目的"],
  ["shotSize", "景别"],
  ["cameraPosition", "机位"],
  ["composition", "构图"],
  ["axisAndBlocking", "轴线/站位"],
  ["visualDescription", "画面描述"],
  ["continuity", "衔接"],
];

const emptyShot = (shotNo) => ({
  shotNo,
  title: `主镜 ${shotNo}`,
  storyPurpose: "",
  shotSize: "",
  cameraPosition: "",
  composition: "",
  axisAndBlocking: "",
  visualDescription: "",
  continuity: "",
  imageUrl: "",
});

const extractJsonObject = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  const text = String(value).trim()
    .replace(/^```json\s*/i, "")
    .replace(/^```\s*/i, "")
    .replace(/```$/i, "")
    .trim();
  try {
    return JSON.parse(text);
  } catch {
    const start = text.indexOf("{");
    const end = text.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(text.slice(start, end + 1));
      } catch {
        return null;
      }
    }
  }
  return null;
};

const shotText = (shot, key, fallback = "") => {
  const value = shot?.[key];
  return value == null ? fallback : String(value);
};

const normalizeShots = (items) => (Array.isArray(items) ? items : [])
  .map((item, index) => ({
    ...emptyShot(index + 1),
    ...item,
    shotNo: index + 1,
    imageUrl: item?.imageUrl || item?.image_url || "",
  }));

const buildAllShotContext = (plan, shots) => shots.map((shot, index) => {
  const no = index + 1;
  return [
    `主镜 ${no}: ${shotText(shot, "title", `主镜${no}`)}`,
    `叙事目的: ${shotText(shot, "storyPurpose")}`,
    `景别: ${shotText(shot, "shotSize")}`,
    `机位: ${shotText(shot, "cameraPosition")}`,
    `构图: ${shotText(shot, "composition")}`,
    `轴线/站位: ${shotText(shot, "axisAndBlocking")}`,
    `画面: ${shotText(shot, "visualDescription")}`,
    `衔接: ${shotText(shot, "continuity")}`,
  ].join("\n");
}).join("\n\n");

const buildSingleShotContext = (shot, index) => [
  `主镜 ${index + 1}: ${shotText(shot, "title", `主镜${index + 1}`)}`,
  `叙事目的: ${shotText(shot, "storyPurpose")}`,
  `景别: ${shotText(shot, "shotSize")}`,
  `机位: ${shotText(shot, "cameraPosition")}`,
  `构图: ${shotText(shot, "composition")}`,
  `轴线/站位: ${shotText(shot, "axisAndBlocking")}`,
  `画面: ${shotText(shot, "visualDescription")}`,
  `衔接: ${shotText(shot, "continuity")}`,
].join("\n");

const buildShotImagePrompt = (plan, shots, targetShot, index) => `生成一张电影工业级主镜 storyboard 参考图，只生成第 ${index + 1} 个主镜。

画面要求：
- 横向 16:9 构图，电影分镜图质感，清晰可读。
- 不要对白、字幕、说明文字、水印、漫画拟声词。
- 必须严格表现该主镜的景别、机位、构图、人物站位、空间轴线、关键道具和情绪。
- 为保持连续性，下面给出全套主镜上下文；但本次只画目标主镜，不要做多格拼图。
- 同一场戏保持人物比例、服装、场景方向、光线方向和 180 度轴线一致。

本集摘要：${plan?.summary || ""}
轴线规则：${plan?.axisNote || ""}

全套主镜上下文：
${buildAllShotContext(plan, shots)}

目标主镜：
${buildSingleShotContext(targetShot, index)}`;

const MasterShotStoryboardPage = ({ projectId, episodeId }) => {
  const toast = useToast();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [episode, setEpisode] = useState(null);
  const [loadingEpisode, setLoadingEpisode] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [generatingIndex, setGeneratingIndex] = useState(null);
  const [rawPlan, setRawPlan] = useState("");
  const [plan, setPlan] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedShotIndex, setSelectedShotIndex] = useState(0);

  const shots = useMemo(() => normalizeShots(plan?.shots), [plan]);
  const scriptText = episode?.scriptContent || "";
  const selectedShot = shots[selectedShotIndex] || shots[0];

  const loadEpisode = useCallback(async () => {
    if (!projectId || !episodeId) return;
    setLoadingEpisode(true);
    try {
      const list = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
      const matched = Array.isArray(list)
        ? list.find((item) => String(item.id) === String(episodeId))
        : null;
      setEpisode(matched || null);
    } catch (error) {
      console.warn("[MasterShot] load episode failed", error);
      toast.error("加载当前集剧本失败");
    } finally {
      setLoadingEpisode(false);
    }
  }, [projectId, episodeId, toast]);

  useEffect(() => {
    loadEpisode();
  }, [loadEpisode]);

  const setShots = useCallback((nextShots) => {
    setPlan((current) => ({
      ...(current || { episodeTitle: episode?.title || "", summary: "", axisNote: "" }),
      shots: normalizeShots(nextShots),
    }));
  }, [episode?.title]);

  const updateShot = useCallback((index, key, value) => {
    const next = shots.map((shot, i) => i === index ? { ...shot, [key]: value } : shot);
    setShots(next);
  }, [setShots, shots]);

  const addShot = () => {
    const next = [...shots, emptyShot(shots.length + 1)];
    setShots(next);
    setSelectedShotIndex(next.length - 1);
  };

  const deleteShot = (index) => {
    if (shots.length <= 1) {
      toast.warning("至少保留一个主镜");
      return;
    }
    const next = shots.filter((_, i) => i !== index);
    setShots(next);
    setSelectedShotIndex(Math.min(index, next.length - 1));
  };

  const handleGeneratePlan = async () => {
    if (!scriptText.trim()) {
      toast.warning("当前集没有剧本，无法拆主镜");
      return;
    }
    setPlanning(true);
    try {
      const provider = localStorage.getItem("ai_provider") || "wangsugem";
      const { result } = await runTaskToCompletion("MASTER_SHOT_PLAN", {
        projectId,
        episodeId,
        episodeTitle: episode?.title || "",
        script: scriptText,
      }, {
        provider,
        projectId,
        episodeId,
        source: "master_shot_plan",
        timeoutMs: 30 * 60 * 1000,
      });
      const text = typeof result === "string" ? result : JSON.stringify(result, null, 2);
      const parsed = extractJsonObject(result);
      if (!parsed || !Array.isArray(parsed.shots)) {
        setRawPlan(text);
        setPlan(null);
        toast.warning("主镜文字已返回，但不是标准结构，可查看原始结果");
      } else {
        const normalized = { ...parsed, shots: normalizeShots(parsed.shots) };
        setRawPlan(JSON.stringify(normalized, null, 2));
        setPlan(normalized);
        setSelectedShotIndex(0);
        toast.success(`主镜文字方案已生成，共 ${normalized.shots.length} 个主镜`);
      }
    } catch (error) {
      toast.error(error.message || "拆主镜失败");
    } finally {
      setPlanning(false);
    }
  };

  const generateShotImage = async (index) => {
    if (!plan || shots.length === 0) {
      toast.warning("请先生成并确认文字主镜方案");
      return;
    }
    const targetShot = shots[index];
    if (!targetShot) return;
    setGeneratingIndex(index);
    try {
      const provider = localStorage.getItem("ai_provider") || "wangsugem";
      const prompt = buildShotImagePrompt(plan, shots, targetShot, index);
      const { result, url } = await runTaskToCompletion("SIMPLE_GENERATE", {
        projectId,
        episodeId,
        provider,
        prompt,
        aspectRatio: "16:9",
        imageQualityTier: normalizeWanxImageQualityTier("4K", provider),
        resourceTypeCode: "master_shot_image",
      }, {
        provider,
        projectId,
        episodeId,
        source: "master_shot_image",
        timeoutMs: 30 * 60 * 1000,
      });
      const imageUrl = url || result?.imageUrl;
      if (!imageUrl) throw new Error("任务完成但没有返回主镜图片");
      setPlan((current) => ({
        ...(current || plan || {}),
        shots: normalizeShots(current?.shots || shots).map((shot, i) => (
          i === index ? { ...shot, imageUrl } : shot
        )),
      }));
      setSelectedShotIndex(index);
      toast.success(`主镜 ${index + 1} 图片已生成`);
    } catch (error) {
      toast.error(error.message || "生成主镜图片失败");
    } finally {
      setGeneratingIndex(null);
    }
  };

  const generateAllImages = async () => {
    if (!shots.length) {
      toast.warning("请先生成并确认文字主镜方案");
      return;
    }
    for (let i = 0; i < shots.length; i += 1) {
      await generateShotImage(i);
    }
  };

  return (
    <div className="w-full h-full overflow-hidden flex flex-col" style={{ background: "var(--color-bg-primary)" }}>
      <div className="px-5 py-4 border-b flex items-center justify-between gap-4" style={{ borderColor: "var(--color-border-primary)" }}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>
              <LayoutGrid size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: "var(--color-text-primary)" }}>主镜参考</h2>
              <p className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>
                先拆文字主镜，编辑确认后按主镜生成大图
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
            className="h-9 px-3 rounded-full border text-xs flex items-center gap-2 cursor-pointer"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", background: "var(--color-bg-secondary)" }}
          >
            {viewMode === "grid" ? <LayoutList size={15} /> : <LayoutGrid size={15} />}
            {viewMode === "grid" ? "列表" : "网格"}
          </button>
          <button
            onClick={addShot}
            disabled={!plan}
            className="h-9 px-3 rounded-full border text-xs flex items-center gap-2 cursor-pointer disabled:opacity-50"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", background: "var(--color-bg-secondary)" }}
          >
            <Plus size={15} />
            添加主镜
          </button>
          <button
            onClick={handleGeneratePlan}
            disabled={planning || loadingEpisode}
            className="h-9 px-4 rounded-full text-xs font-semibold flex items-center gap-2 cursor-pointer disabled:opacity-60"
            style={{ background: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
          >
            {planning ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            拆主镜
          </button>
          <button
            onClick={generateAllImages}
            disabled={generatingIndex !== null || shots.length === 0}
            className="h-9 px-4 rounded-full text-xs font-semibold flex items-center gap-2 cursor-pointer disabled:opacity-50"
            style={{ background: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
          >
            {generatingIndex !== null ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />}
            生成全部图片
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-[260px_minmax(0,1fr)_460px] gap-4 p-4">
        <aside className="rounded-xl border min-h-0 flex flex-col overflow-hidden" style={{ borderColor: "var(--color-border-primary)", background: "var(--color-bg-secondary)" }}>
          <div className="p-4 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
              <BookOpen size={16} />
              当前集剧本
            </div>
            <div className="mt-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
              {episode?.title || "未命名集"} · {scriptText.length.toLocaleString()} 字
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4 text-sm leading-7 whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>
            {loadingEpisode ? "加载中..." : scriptText || "当前集暂无剧本"}
          </div>
        </aside>

        <main className="rounded-xl border min-h-0 flex flex-col overflow-hidden" style={{ borderColor: "var(--color-border-primary)", background: "var(--color-bg-secondary)" }}>
          <div className="p-4 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)" }}>
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>文字主镜方案</div>
              <input
                value={plan?.summary || ""}
                onChange={(e) => setPlan((current) => ({ ...(current || {}), summary: e.target.value }))}
                placeholder="本集主镜设计摘要"
                className="mt-2 w-full rounded-lg px-3 py-2 text-xs outline-none"
                style={{ background: "var(--color-bg-primary)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border-primary)" }}
                disabled={!plan}
              />
              <textarea
                value={plan?.axisNote || ""}
                onChange={(e) => setPlan((current) => ({ ...(current || {}), axisNote: e.target.value }))}
                placeholder="轴线与换轴规则"
                className="mt-2 w-full rounded-lg px-3 py-2 text-xs outline-none resize-none"
                style={{ background: "var(--color-bg-primary)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border-primary)" }}
                rows={2}
                disabled={!plan}
              />
            </div>
            {shots.length > 0 && (
              <span className="text-xs px-2 py-1 rounded-full shrink-0" style={{ color: theme.colors.accentPrimary, background: "var(--color-accent-light)" }}>
                {shots.length} 个主镜
              </span>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {shots.length > 0 ? (
              <div key={viewMode} className={`animate-in fade-in duration-200 ${viewMode === "grid" ? "grid grid-cols-1 2xl:grid-cols-2 gap-3" : "flex flex-col gap-3"}`}>
                {shots.map((shot, index) => (
                  <ShotPlanCard
                    key={`${shot.shotNo}-${index}`}
                    shot={shot}
                    index={index}
                    selected={selectedShotIndex === index}
                    generating={generatingIndex === index}
                    onSelect={() => setSelectedShotIndex(index)}
                    onChange={(key, value) => updateShot(index, key, value)}
                    onDelete={() => deleteShot(index)}
                    onGenerate={() => generateShotImage(index)}
                  />
                ))}
              </div>
            ) : rawPlan ? (
              <pre className="text-xs leading-6 whitespace-pre-wrap rounded-xl p-4" style={{ color: "var(--color-text-secondary)", background: "var(--color-bg-primary)" }}>
                {rawPlan}
              </pre>
            ) : (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>
                    <LayoutGrid size={24} />
                  </div>
                  <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>还没有主镜方案</div>
                  <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>点击“拆主镜”开始</div>
                </div>
              </div>
            )}
          </div>
        </main>

        <aside className="rounded-xl border min-h-0 flex flex-col overflow-hidden" style={{ borderColor: "var(--color-border-primary)", background: "var(--color-bg-secondary)" }}>
          <div className="p-4 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
            <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>主镜图片</div>
            <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
              {selectedShot ? `当前：${String(selectedShot.shotNo).padStart(2, "0")} ${selectedShot.title}` : "确认文字方案后生成图片"}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {shots.length > 0 ? (
              <div className="space-y-4">
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", background: "var(--color-bg-primary)" }}>
                  <div className="px-3 py-2 flex items-start justify-between gap-3 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold leading-5 break-words" style={{ color: "var(--color-text-primary)" }}>
                        {String((selectedShotIndex || 0) + 1).padStart(2, "0")} · {selectedShot?.title || `主镜 ${(selectedShotIndex || 0) + 1}`}
                      </div>
                      <div className="text-2xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                        选中主镜大图预览
                      </div>
                    </div>
                    <button
                      onClick={() => generateShotImage(selectedShotIndex)}
                      disabled={generatingIndex !== null}
                      className="h-8 px-3 rounded-lg border text-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 shrink-0"
                      style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
                    >
                      {generatingIndex === selectedShotIndex ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
                      生成
                    </button>
                  </div>
                  {selectedShot?.imageUrl ? (
                    <img src={selectedShot.imageUrl} alt={selectedShot.title || "主镜图片"} className="w-full aspect-video object-contain" />
                  ) : (
                    <div className="aspect-video flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>
                      {generatingIndex === selectedShotIndex ? <Loader2 size={30} className="animate-spin" /> : <ImageIcon size={32} />}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {shots.map((shot, index) => (
                    <button
                      key={`image-${shot.shotNo}-${index}`}
                      onClick={() => setSelectedShotIndex(index)}
                      className="block w-full text-left rounded-xl border overflow-hidden cursor-pointer"
                      style={{
                        borderColor: selectedShotIndex === index ? theme.colors.accentPrimary : "var(--color-border-primary)",
                        background: "var(--color-bg-primary)",
                      }}
                    >
                      {shot.imageUrl ? (
                        <img src={shot.imageUrl} alt={shot.title || `主镜 ${index + 1}`} className="w-full aspect-video object-cover" />
                      ) : (
                        <div className="aspect-video flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>
                          {generatingIndex === index ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={20} />}
                        </div>
                      )}
                      <div className="px-2 py-2">
                        <div className="text-xs font-semibold leading-5 break-words" style={{ color: "var(--color-text-primary)" }}>
                          {String(index + 1).padStart(2, "0")} · {shot.title || `主镜 ${index + 1}`}
                        </div>
                        <div className="text-2xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                          {shot.imageUrl ? "已生成" : "未生成"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-full rounded-xl border border-dashed flex items-center justify-center text-center p-8" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
                <div>
                  <ImageIcon size={28} className="mx-auto mb-3 opacity-60" />
                  <div className="text-sm">确认文字方案后生成图片</div>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

const ShotPlanCard = ({
  shot,
  selected,
  generating,
  onSelect,
  onChange,
  onDelete,
  onGenerate,
}) => (
  <div
    className="rounded-xl border p-4 cursor-pointer"
    onClick={onSelect}
    style={{
      borderColor: selected ? "var(--color-accent-primary)" : "var(--color-border-primary)",
      background: "var(--color-bg-primary)",
    }}
  >
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0" style={{ background: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
        {String(shot.shotNo).padStart(2, "0")}
      </div>
      <div className="min-w-0 flex-1">
        <input
          value={shotText(shot, "title")}
          onChange={(e) => onChange("title", e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="w-full text-sm font-semibold rounded-md px-2 py-1 outline-none"
          style={{ color: "var(--color-text-primary)", background: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}
        />
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onGenerate(); }}
        disabled={generating}
        className="w-8 h-8 rounded-lg border flex items-center justify-center cursor-pointer disabled:opacity-60 shrink-0"
        style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
        title="生成这个主镜图片"
      >
        {generating ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="w-8 h-8 rounded-lg border flex items-center justify-center cursor-pointer shrink-0"
        style={{ borderColor: "color-mix(in srgb, var(--color-error) 45%, transparent)", color: "var(--color-error)" }}
        title="删除主镜"
      >
        <Trash2 size={14} />
      </button>
    </div>

    <div className="mt-3 grid grid-cols-2 gap-2">
      {PLAN_FIELDS.slice(1, 6).map(([key, label]) => (
        <EditableField key={key} label={label} value={shotText(shot, key)} onChange={(value) => onChange(key, value)} />
      ))}
    </div>
    <div className="mt-2 grid grid-cols-1 gap-2">
      {PLAN_FIELDS.slice(6).map(([key, label]) => (
        <EditableField key={key} label={label} value={shotText(shot, key)} onChange={(value) => onChange(key, value)} multiline />
      ))}
    </div>
  </div>
);

const EditableField = ({ label, value, onChange, multiline }) => (
  <label className="block min-w-0">
    <div className="text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>{label}</div>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        rows={3}
        className="w-full rounded-lg px-3 py-2 text-xs leading-5 outline-none resize-y"
        style={{ color: "var(--color-text-secondary)", background: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}
      />
    ) : (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        rows={2}
        className="w-full rounded-lg px-3 py-2 text-xs leading-5 outline-none resize-y"
        style={{ color: "var(--color-text-secondary)", background: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}
      />
    )}
  </label>
);

export default MasterShotStoryboardPage;
